/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type AxiosInstance } from 'axios';
import { requestBaseURL } from '@/config/serviceAPI';
import type {
  BrandShelfResponseModel,
  GetBrandListResultsUsingGetParamsModel,
} from '@/api/types/browse-search-types';
import { getAxios } from '@/lib/axios';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import {
  type BaseInfiniteMergeFunction,
  type BaseMergedData,
  createInfiniteQuery,
} from '@/utils/query/createInfiniteQuery';
import { useLocale } from '@/hooks/useLocale';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { type QueryClient } from '@tanstack/react-query';
import { parseUrl } from '@/utils/urlHelpers';
import { useMapFacet } from '@/hooks/useMapFacet';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import type { GetProductBrandDataOptions, MappedBrandShelfResults } from '../interface';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useQueryResultOnSuccess } from '@/hooks/query/useQueryResultOnSuccess';
import { useRouter } from 'next/navigation';
import { useDeviceType } from '@/utils/useDeviceType';
import { getShelfRequestStoreId, getBotEnabledFacetPath } from '@/utils/getShelfRequestStoreId';

const URL = `${requestBaseURL}/external/product-discovery/browse-search/v1/brand-shelves`;

type OptionsWithoutPage = Omit<GetProductBrandDataOptions, 'pageNumber'>;

const selector = (
  { brandShelfResults, redirectUrl, xFusionQueryId }: BrandShelfResponseModel,
  mapFacet: ReturnType<typeof useMapFacet>,
): MappedBrandShelfResults => {
  return {
    redirectLocation: redirectUrl,
    recordsPerPage: brandShelfResults?.recordsPerPage ?? 12,
    total: brandShelfResults?.totalNumberOfRecords,
    quickFilters: brandShelfResults?.quickFilters,
    firstRecNum: brandShelfResults?.firstRecordNumber,
    lastRecNum: brandShelfResults?.lastRecordNumber,
    staticNavigation: brandShelfResults?.facets?.map(mapFacet) ?? [],
    brandName: brandShelfResults?.brandName ?? '',
    records: brandShelfResults?.skuRecords?.map((record) => ({
      ...record,
      active: record.activeFlag,
      alternatePartNumber: record.alternatePartNumber,
      brandName: record.brandName,
      seoUrl: record.productDetailsPageUrl,
      imageUrl: record.itemImageUrl ?? '',
      productImageUrl: record.itemImageUrl ?? '',
      description: record.itemDescription,
      skuNumber: Number(record.itemId),
      locationFilter: record.itemLocation || record.location,
      lineCode: record.lineCode,
      systemCode: Number(record.originalSystemCode),
      partNumber: record.partNumber,
      productRepositoryId: record.eCommerceProductId,
      productId: record.eCommerceProductId,
      quickNote: record.quickNote,
      techNote: record.technicalNote,
      recordType: record.recordType,
      skuReviewEnabled: record.reviewEnabledFlag,
      vehicleFitment: record.vehicleFitment?.vehicleFit,
      vehicleFitmentLabel: record.vehicleFitmentLabel,
      warrantyMonths: record.warrantyMonths,
      brand: record.brandName ?? '',
      name: record.itemDescription,
      productReviewsEnabled: record.reviewEnabledFlag,
      vehicleFit: record.vehicleFitmentFlag ?? !!record.vehicleFitment?.vehicleFit,
      vehicleSpecific: record.vehicleSpecificFlag ?? false,
      originalPartTypeId: record.originalPartTypeId ?? '',
    })),
    xFusionQueryId,
  };
};

const getBrandProductData = async (
  options: OptionsWithoutPage,
  pageNumber: number,
  axiosInstance?: AxiosInstance,
) => {
  const { locale, ...rest } = options;
  const country = getCountryFromLocale(locale);
  const customerType = 'B2C';
  const salesChannel = 'ECOMM';

  const params: GetBrandListResultsUsingGetParamsModel = {
    ...rest,
    pageNumber,
    country,
    customerType,
    salesChannel,
  };
  const response = await getAxios(axiosInstance).get<BrandShelfResponseModel>(URL, {
    params,
  });

  return response.data;
};

const selectInfiniteQueryData: BaseInfiniteMergeFunction<BrandShelfResponseModel, number> = (
  data,
) => {
  const mergedData = data.pages.reduce<BrandShelfResponseModel>(
    (acc, page) => {
      if (page.brandShelfResults) {
        return {
          ...acc,
          brandShelfResults: {
            ...acc.brandShelfResults,
            ...page.brandShelfResults,
            skuRecords: [
              ...(acc.brandShelfResults?.skuRecords ?? []),
              ...(page.brandShelfResults.skuRecords ?? []),
            ],
          },
          redirectUrl: page.redirectUrl,
          redirectPageType: page.redirectPageType,
          xFusionQueryId: page.xFusionQueryId,
        };
      }
      return acc;
    },
    { brandShelfResults: { skuRecords: [] } } as unknown as BrandShelfResponseModel,
  );

  return {
    mergedData,
    pages: data.pages,
    pageParams: data.pageParams,
    pageNumber: data.pages.length,
  };
};

const {
  useData: useBrandProducts,
  prefetch: prefetchProductBrand,
  query: productBrandQuery,
  getFromCache,
} = createInfiniteQuery<
  BrandShelfResponseModel,
  OptionsWithoutPage,
  number,
  BaseMergedData<BrandShelfResponseModel, number>
>(['brandProducts'], getBrandProductData, {
  initialPageParam: 1,
  getNextPageParam: (lastPage, allPages) => {
    const totalRecords = lastPage.brandShelfResults?.totalNumberOfRecords ?? 0;
    const currentPageNumber = allPages.length;

    // Count actual records loaded across all pages
    const loadedRecords = allPages.reduce((sum, page) => {
      return sum + (page.brandShelfResults?.skuRecords?.length ?? 0);
    }, 0);

    // Only fetch next page if we haven't loaded all records yet
    if (loadedRecords < totalRecords) {
      return currentPageNumber + 1;
    }
    return undefined;
  },
  selectInfiniteQueryData,
});

export const useBrandProductData = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const locale = useLocale();
  const isBot = useDeviceType() === 'bot';
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const mapFacet = useMapFacet();
  const { acesEnabled } = useAcesFlags();
  const preferredVehicle = usePreferredVehicle();
  const { data: storeDetailsData } = useStoreDetailsData();
  const storeNumber = storeDetailsData?.storeNumber;
  const vehicleId = preferredVehicle?.catalogVehicleId;
  const { sort = [], facet, minPrice, maxPrice } = legacyRouter.query;
  const sortArr = typeof sort === 'string' ? sort.split('-') : sort;
  const sortOrder = sortArr[sortArr.length - 1];
  const sortFieldName = sortArr.slice(0, sortArr.length - 1).join('-');

  const path = legacyRouter.asPath;
  const parsedUrl = parseUrl(path);
  const botEnabledFacetPath = getBotEnabledFacetPath(parsedUrl.url);

  const queryOptions: OptionsWithoutPage = {
    locale,
    vehicleId,
    recordsPerPage: 24,
    minPrice: minPrice ? String(minPrice) : undefined,
    maxPrice: maxPrice ? String(maxPrice) : undefined,
    facet: Array.isArray(facet) ? facet.join(',') : facet,
    sort: sortFieldName && sortOrder ? `${sortFieldName}-${sortOrder}` : undefined,
    storeId: getShelfRequestStoreId(storeNumber, isBot, botEnabledFacetPath),
    seoUrl: parsedUrl.url,
    preview: showXMPreviewDate(),
    mapFacet: mapFacet,
  };

  const infiniteQueryResult = useBrandProducts({
    queryOptions,
    enabled: enabled && !acesEnabled,
  });

  useQueryResultOnSuccess(
    infiniteQueryResult,
    (data) => {
      const lastPage = data.pages[data.pages.length - 1];
      if (lastPage.redirectUrl) {
        router.replace(lastPage.redirectUrl);
      }
    },
    true,
  );

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    status,
    refetch,
    fetchStatus,
    isSuccess,
    isError,
    isFetching,
    isPending,
    isFetchingNextPage,
  } = infiniteQueryResult;
  const { mergedData, pages, pageNumber = 1 } = infiniteData ?? {};

  const selectedData = selector(
    mergedData ?? ({ brandShelfResults: { skuRecords: [] } } as unknown as BrandShelfResponseModel),
    mapFacet,
  );

  return {
    pages,
    data: selectedData,
    pageNumber,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    status,
    refetch,
    fetchStatus,
    isSuccess,
    isError,
    isFetchingNextPage,
  };
};

export const prefetchBrandInfinityData = (
  queryClient: QueryClient,
  options: OptionsWithoutPage,
  axiosInstance: AxiosInstance,
) => {
  return prefetchProductBrand(queryClient, options, axiosInstance);
};

export const getBrandProductDataFromCache = (
  queryClient: QueryClient,
  options: OptionsWithoutPage,
) => {
  return getFromCache(queryClient, options);
};

export { productBrandQuery };
